import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import { Search, Tag, User } from 'lucide-react';

const Blog = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Blog posts data - STRATEGIC IMAGE SELECTION
  const blogPosts = [
    {
      id: 'founding-day',
      title: 'Philly Grit, a Corgi Named Kelce & the Birth of Poser AI™',
      excerpt: 'A breakfast in Florida, an Iverson hoodie, and the decision to finally bet on myself.',
      category: 'Founder Story',
      tags: ['Poser AI™', 'Founder Story', '🦅 Philly', '🎖️ Veteran-Owned'],
      date: 'November 15, 2025',
      author: 'Jim Bukowski',
      image: '/jim-bukowski-profile.jpg', // Personal story = profile photo
      featured: true,
      readTime: '8 min read'
    },
    {
      id: 'company',
      title: 'WestBay Group: Built for Independence',
      excerpt: 'A Veteran-Owned Cloud Solutions Provider serving direct clients and strategic partners',
      category: 'Company',
      tags: ['Company', 'Cloud Security', '🎖️ Veteran-Owned', 'AWS Certified'],
      date: 'November 2025',
      author: 'Jim Bukowski',
      image: '/poser-ai-hero.png', // Company/product story = product hero
      featured: false,
      readTime: '6 min read'
    },
    {
      id: 'investment',
      title: 'Join Us in Transforming Cloud Security',
      excerpt: 'Seeking $500K seed funding to scale Poser AI™',
      category: 'Investment',
      tags: ['Investment', 'Seed Round', 'Patent Pending', 'Cloud Security'],
      date: 'November 2025',
      author: 'Jim Bukowski',
      image: '/poser-ai-hero.png', // Investment/product opportunity = product hero
      featured: true,
      readTime: '10 min read'
    }
  ];

  // Categories
  const categories = ['All', 'Founder Story', 'Company', 'Investment', 'Technical'];

  // Filter posts
  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredPosts = filteredPosts.filter(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured);

  return (
    <Layout>
      <div className="blog-magazine-container">
        {/* Hero Header */}
        <div className="blog-hero">
          <div className="blog-hero-content">
            <div className="blog-hero-kicker">THE WEST BAY JOURNAL</div>
            <h1 className="blog-hero-title">Stories, Grit & Real Talk</h1>
            <p className="blog-hero-subtitle">
              No corporate BS. Just authentic stories from the trenches of cloud security,
              entrepreneurship, and betting on yourself.
            </p>
          </div>
        </div>

        {/* Search & Filter Bar */}
        <div className="blog-controls">
          <div className="blog-search">
            <Search size={20} />
            <input
              type="text"
              placeholder="Search stories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="blog-search-input"
            />
          </div>
          
          <div className="blog-categories">
            {categories.map(cat => (
              <button
                key={cat}
                className={`blog-category-btn ${
                  selectedCategory === cat ? 'active' : ''
                }`}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Featured Posts Section */}
        {featuredPosts.length > 0 && (
          <section className="featured-section">
            <div className="section-header">
              <h2 className="section-title">Featured Stories</h2>
              <div className="section-divider"></div>
            </div>
            <div className="featured-grid">
              {featuredPosts.map(post => (
                <article key={post.id} className="featured-card" onClick={() => navigate(`/blog/${post.id}`)}>
                  <div className="featured-card-image">
                    <img 
                      src={post.image} 
                      alt={post.title} 
                      className={post.id === 'founding-day' ? 'vangogh-portrait' : ''}
                    />
                    <div className="featured-badge">FEATURED</div>
                  </div>
                  <div className="featured-card-content">
                    <div className="card-meta">
                      <span className="card-category">{post.category}</span>
                      <span className="card-date">{post.date}</span>
                    </div>
                    <h3 className="card-title">{post.title}</h3>
                    <p className="card-excerpt">{post.excerpt}</p>
                    <div className="card-footer">
                      <div className="card-author">
                        <User size={16} />
                        <span>{post.author}</span>
                      </div>
                      <span className="card-read-time">{post.readTime}</span>
                    </div>
                    <div className="card-tags">
                      {post.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="card-tag">
                          <Tag size={12} />
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* All Stories Section (Tozi-style 2-column grid) */}
        <section className="all-stories-section">
          <div className="section-header">
            <h2 className="section-title">All Stories</h2>
            <div className="section-divider"></div>
          </div>
          
          {filteredPosts.length === 0 ? (
            <div className="no-results">
              <p>No stories found matching your search.</p>
            </div>
          ) : (
            <div className="blog-grid">
              {filteredPosts.map(post => (
                <article key={post.id} className="blog-card" onClick={() => navigate(`/blog/${post.id}`)}>
                  <div className="blog-card-image">
                    <img 
                      src={post.image} 
                      alt={post.title} 
                      className={post.id === 'founding-day' ? 'vangogh-portrait' : ''}
                    />
                    {post.featured && <div className="featured-badge-small">★</div>}
                  </div>
                  <div className="blog-card-content">
                    <div className="card-meta">
                      <span className="card-category">{post.category}</span>
                      <span className="card-date">{post.date}</span>
                    </div>
                    <h3 className="card-title">{post.title}</h3>
                    <p className="card-excerpt">{post.excerpt}</p>
                    <div className="card-footer">
                      <div className="card-author">
                        <User size={16} />
                        <span>{post.author}</span>
                      </div>
                      <span className="card-read-time">{post.readTime}</span>
                    </div>
                    <div className="card-tags">
                      {post.tags.map(tag => (
                        <span key={tag} className="card-tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {/* Author Section - ALWAYS profile photo for personal connection */}
        <section className="author-section">
          <div className="author-card">
            <div className="author-image">
              <img src="/jim-bukowski-profile.jpg" alt="Jim Bukowski" className="vangogh-portrait" />
            </div>
            <div className="author-content">
              <h3 className="author-name">Jim Bukowski</h3>
              <div className="author-title">Founder & Chief Architect</div>
              <p className="author-bio">
                Originally from <strong>Philadelphia</strong>, I'm a U.S. Navy veteran with 30+ years in cloud security and DevOps. 
                AWS Certified Solutions Architect. HashiCorp Terraform Certified. Built solutions for Fortune 500 companies. 
                Now building <strong>Poser AI™</strong> with my corgi Kelce by my side. Self-funded, veteran-owned, 
                and betting everything on making enterprise security accessible to everyone.
              </p>
              <div className="author-badges">
                <span className="author-badge">🦅 Philly Born</span>
                <span className="author-badge">🎖️ U.S. Navy Veteran</span>
                <span className="author-badge">☁️ AWS Certified</span>
                <span className="author-badge">🐶 Corgi Dad</span>
              </div>
            </div>
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="newsletter-section">
          <div className="newsletter-content">
            <h2 className="newsletter-title">Stay in the Loop</h2>
            <p className="newsletter-subtitle">
              Get updates on Poser AI™ development, cloud security insights, and real talk about building a startup. 
              No spam. Just grit.
            </p>
            <div className="newsletter-form">
              <input
                type="email"
                placeholder="your.email@example.com"
                className="newsletter-input"
              />
              <button className="newsletter-btn">Subscribe</button>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default Blog;